// LLM generated - Model GPT-5.2
import { useMantineTheme } from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { memo, useCallback } from 'react';

import silhouetteLight from '~/components/assets/silhouette-light.png';
import Image from '~/domains/common/components/Image/Image';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import PlayerCardTag from '~/domains/contest/domains/gamecenter/domains/player/components/GamecenterPlayerDetail/GamecenterPlayerDetailTag';

import { getLineLabelText } from '../../../utils/getLineLabelText';
import { getPickButtonVariant } from '../../../utils/getPickButtonVariant';
import { useIsQuickPicksV2 } from '../../../hooks/useIsQuickPicksV2';
import { formatProbabilityPercent } from '../../../utils/formatProbabilityPercent';
import { usePickButtonStyles } from '../../pickButton.styles';
import { useLeagueIconUrl } from '../../../hooks/useLeagueIconUrl';
import { getPlayerTeamFirstMatchup } from '../../../utils/playerTeamId.utils';
import { MatchupLabel } from '../MatchupLabel';
import type { PlayerCellProps } from '../PlayerCell/PlayerCell';
import { LeagueFallbackImage } from '../LeagueFallbackImage';

import { usePopularPickCardStyles } from './PopularPickCard.styles';

export { POPULAR_PICK_CARD_WIDTH_PX } from './PopularPickCard.styles';

const PopularPickCard = memo(
  ({
    pick,
    className,
    hideTeamLogo = false,
  }: {
    pick: PlayerCellProps;
    className?: string;
    hideTeamLogo?: boolean;
  }) => {
    const { classes, cx } = usePopularPickCardStyles();
    const { classes: pickButtonClasses } = usePickButtonStyles();
    const isQuickPicksV2 = useIsQuickPicksV2();
    const theme = useMantineTheme();
    const { t } = useTranslation('contest');
    const { injury } = pick;
    const leagueIconUrl = useLeagueIconUrl(pick.league);
    const overPercent = isQuickPicksV2 ? formatProbabilityPercent(pick.overProbability) : null;
    const underPercent = isQuickPicksV2 ? formatProbabilityPercent(pick.underProbability) : null;

    const handleMore = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (pick.isDisabledForAlternate) {
          return;
        }
        pick.onPick?.({ ...pick, direction: 'over' });
      },
      [pick]
    );

    const handleLess = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (pick.isDisabledForAlternate) {
          return;
        }
        pick.onPick?.({ ...pick, direction: 'under' });
      },
      [pick]
    );

    const handleCardClick = useCallback(() => {
      pick.onHeaderClick?.(pick.id);
    }, [pick]);

    const timeLabel = pick.startDateAsDate
      ? format(pick.startDateAsDate, 'h:mmaaa')
      : pick.startDate;
    const matchup = getPlayerTeamFirstMatchup({
      awayTeam: pick.awayTeam,
      homeTeam: pick.homeTeam,
      playerTeamId: pick.playerTeamId,
      league: pick.league,
    });

    return (
      <div
        className={cx(classes.card, className, {
          [classes.cardDisabled]: !!pick.isDisabledForAlternate,
          [classes.cardSelected]: !pick.isAlternate && !!pick.direction,
          [classes.cardSelectedAlternate]: !!pick.isAlternate && !!pick.direction,
        })}
        role="button"
        tabIndex={0}
        aria-label={pick.playerName}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        {!hideTeamLogo && (
          <Image
            src={pick.leagueImage}
            alt=""
            width={400}
            height={400}
            className={classes.cardTeamLogo}
            fallbackImageUrl={leagueIconUrl}
            aria-hidden
          />
        )}
        <div className={classes.cardGradient} />

        <div className={classes.cardUpper}>
          <div className={classes.cardLeagueRow}>
            <div className={classes.cardLeague}>
              <span className={classes.cardLeagueIcon}>
                <LeagueFallbackImage
                  league={pick.league}
                  leagueIconUrl={leagueIconUrl}
                  size={16}
                  color={theme.colors.gray2[3]}
                  applySolidColor
                />
              </span>
              {pick.position ? (
                <Text variant="subhead-x-small" weight={900} className={classes.cardLeaguePosition}>
                  {pick.position}
                </Text>
              ) : null}
            </div>
          </div>
          <div className={classes.cardAvatarWrap}>
            <div className={classes.cardPlayerImage}>
              <Image
                src={pick.playerImage}
                width={64}
                height={64}
                fallbackComponent={
                  <Image src={silhouetteLight} width={64} height={64} alt={pick.playerName} />
                }
                alt={pick.playerName}
              />
            </div>
            {injury ? <PlayerCardTag className={classes.injuryTagAvatar} tag={{ injury }} /> : null}
          </div>
        </div>

        <div className={classes.cardPlayerInfo}>
          <Text
            variant="headline-medium"
            weight={800}
            color={theme.colors.gray2[9]}
            className={classes.cardPlayerName}
          >
            {pick.playerName}
          </Text>
          <div className={classes.cardMatchup}>
            <Text
              variant="subhead-x-small"
              color={theme.colors.gray2[3]}
              className={classes.cardMatchupText}
            >
              {matchup ? (
                <>
                  <MatchupLabel matchup={matchup} /> • {timeLabel}
                </>
              ) : (
                `${pick.gameDescription} • ${timeLabel}`
              )}
            </Text>
          </div>
        </div>

        <div className={classes.cardStat}>
          <Text
            variant="display-headline-condensed-xxl"
            color={theme.colors.gray2[6]}
            className={classes.cardStatValue}
          >
            {pick.line.value}
          </Text>
          <Text
            variant="subhead-small"
            color={theme.colors.gray2[4]}
            className={classes.cardStatLabel}
          >
            {getLineLabelText(pick.line)}
          </Text>
        </div>

        <div className={classes.cardButtons}>
          <Button
            variant={getPickButtonVariant(pick.direction === 'over', !!pick.isAlternate)}
            size="small"
            className={cx(classes.cardButton, isQuickPicksV2 && classes.cardButtonWithOdds)}
            onClick={handleMore}
            disabled={!!pick.isDisabledForAlternate}
            noWrap
          >
            <span className={classes.cardButtonIcon}>
              <IconArrowUp size={16} />
            </span>
            <span className={pickButtonClasses.textColumn}>
              <Text variant="headline-x-small">{t('quickPick.board.playerCard.more')}</Text>
              {overPercent && (
                <Text variant="headline-x-small" color="gray2.4">
                  {overPercent}
                </Text>
              )}
            </span>
          </Button>
          <Button
            variant={getPickButtonVariant(pick.direction === 'under', !!pick.isAlternate)}
            size="small"
            className={cx(classes.cardButton, isQuickPicksV2 && classes.cardButtonWithOdds)}
            onClick={handleLess}
            disabled={!!pick.isDisabledForAlternate}
            noWrap
          >
            <span className={classes.cardButtonIcon}>
              <IconArrowDown size={16} />
            </span>
            <span className={pickButtonClasses.textColumn}>
              <Text variant="headline-x-small">{t('quickPick.board.playerCard.less')}</Text>
              {underPercent && (
                <Text variant="headline-x-small" color="gray2.4">
                  {underPercent}
                </Text>
              )}
            </span>
          </Button>
        </div>

        <div className={classes.cardInnerShadow} />
      </div>
    );
  }
);

PopularPickCard.displayName = 'PopularPickCard';

export { PopularPickCard };
